/**
 * PostHog product-analytics wrapper.
 *
 * Single choke-point for every PostHog call. Feature code must import from
 * this module only — never from `posthog-js` directly. That keeps the kill
 * switch clean (unset VITE_POSTHOG_KEY → redeploy → all events no-op) and
 * gives us one place to enforce the event taxonomy.
 *
 * Init is LAZY. `posthog.init()` does NOT run at module import. Instead,
 * AnalyticsInitializer calls `analytics.bootstrap(cachedUserId?)` after auth
 * hydration resolves. This lets us pass `bootstrap: { distinctID: cachedUserId }`
 * to PostHog so an authenticated cold start skips the anon→identify merge
 * that would otherwise fire on every iOS app launch. Any capture/identify
 * call fired before bootstrap completes is queued and flushed once init
 * runs — so the module API stays stable for callers.
 *
 * Stasht does not request App Tracking Transparency for this path because it
 * does not track users across other companies' apps or websites.
 */

import { Capacitor } from "@capacitor/core";
import posthog from "posthog-js";
import { CONFIG, APP_VERSION } from "@/config";
import { API_CONFIG, ENDPOINTS, SECURITY_HEADERS } from "@/lib/api/config";
import { tokenManager } from "@/utils/token-manager";
import { IS_CONNECTION_SESSION } from "@/utils/connection-session";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://us.i.posthog.com";

type Source = "web" | "ios" | "android";
type Jsonish = string | number | boolean | null | Jsonish[] | { [key: string]: Jsonish };

const ACTIVITY_ACTION_RE = /^[a-z][a-z0-9_]{0,79}$/;
const ACTIVITY_LEDGER_ENABLED = CONFIG.API_ENV === "production" && !CONFIG.IS_DEVELOPMENT;
const SERVER_LEDGER_EVENTS = new Set(["account_deleted"]);
const SENSITIVE_PROPERTY_RE = /(password|token|secret|authorization|cookie|email|phone|address|query|search)/i;
const MAX_METADATA_KEYS = 50;
const MAX_STRING_LENGTH = 512;

function resolveSource(): Source {
  if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    if (platform === "ios" || platform === "android") return platform;
  }
  return "web";
}

function normalizeActivityAction(event: string): string | null {
  const action = event
    .trim()
    .replace(/^\$+/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return ACTIVITY_ACTION_RE.test(action) ? action : null;
}

function sanitizeMetadataKey(rawKey: string): string | null {
  const key = (rawKey === "$insert_id" ? "insert_id" : rawKey)
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return key || null;
}

function sanitizeActivityValue(value: unknown, depth = 0): Jsonish | undefined {
  if (depth > 3 || typeof value === "undefined" || typeof value === "function" || typeof value === "symbol") {
    return undefined;
  }
  if (value === null || typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") return value.slice(0, MAX_STRING_LENGTH);
  if (Array.isArray(value)) {
    return value
      .slice(0, 20)
      .map(item => sanitizeActivityValue(item, depth + 1))
      .filter((item): item is Jsonish => typeof item !== "undefined");
  }
  if (typeof value === "object") {
    const output: Record<string, Jsonish> = {};
    for (const [rawKey, rawValue] of Object.entries(value).slice(0, MAX_METADATA_KEYS)) {
      const key = sanitizeMetadataKey(rawKey);
      if (!key) continue;
      if (!key.startsWith("has_") && SENSITIVE_PROPERTY_RE.test(key)) {
        if (rawValue) output[`has_${key}`] = true;
        continue;
      }
      const sanitizedValue = sanitizeActivityValue(rawValue, depth + 1);
      if (typeof sanitizedValue !== "undefined") output[key] = sanitizedValue;
    }
    return output;
  }
  return String(value).slice(0, MAX_STRING_LENGTH);
}

function sanitizeActivityProperties(event: string, properties?: Record<string, unknown>): Record<string, Jsonish> {
  const metadata: Record<string, Jsonish> = {
    client_event_name: event,
    app_version: APP_VERSION,
    source: resolveSource(),
    native_platform: Capacitor.getPlatform(),
  };

  if (!properties) return metadata;

  for (const [rawKey, rawValue] of Object.entries(properties).slice(0, MAX_METADATA_KEYS)) {
    const key = sanitizeMetadataKey(rawKey);
    if (!key) continue;
    if (!key.startsWith("has_") && SENSITIVE_PROPERTY_RE.test(key)) {
      if (rawValue) metadata[`has_${key}`] = true;
      continue;
    }
    const sanitizedValue = sanitizeActivityValue(rawValue);
    if (typeof sanitizedValue !== "undefined") metadata[key] = sanitizedValue;
  }

  return metadata;
}

async function postActivityLedgerEvent(event: string, properties?: Record<string, unknown>): Promise<void> {
  const action = normalizeActivityAction(event);
  if (!action || SERVER_LEDGER_EVENTS.has(action)) return;

  const accessToken = await tokenManager.ensureFreshAccessToken();
  if (!accessToken) return;

  const headers: HeadersInit = {
    "content-type": "application/json",
    authorization: `Bearer ${accessToken}`,
    "x-stasht-client-platform": resolveSource(),
  };
  const apiKey = SECURITY_HEADERS["x-api-key"];
  if (apiKey) headers["x-api-key"] = apiKey;

  const response = await fetch(`${API_CONFIG.BASE_URL}${ENDPOINTS.AUTH.ACTIVITY}`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      action,
      metadata: sanitizeActivityProperties(event, properties),
    }),
    credentials: "omit",
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    console.warn(`[analytics] activity ledger write failed: ${response.status} ${body.slice(0, 200)}`);
  }
}

function captureActivityLedger(event: string, properties?: Record<string, unknown>): void {
  if (!ACTIVITY_LEDGER_ENABLED) return;
  void postActivityLedgerEvent(event, properties).catch(err => {
    console.warn("[analytics] activity ledger call failed", err);
  });
}

// $insert_id generator. Prefers crypto.randomUUID (available on modern
// browsers + Capacitor iOS WebView 15.4+ / Android WebView 92+). Falls
// back to a timestamp + entropy string on older devices — PostHog accepts
// any string as an insert_id, uniqueness is the only requirement, and the
// fallback's collision probability within a 24h dedup window is effectively
// zero (timestamp ms + 12 chars of base36 random = ~2^80 keyspace).
// Without this fallback, pre-iOS-15.4 devices throw on crypto.randomUUID
// and safely() catches, dropping the ENTIRE capture call.
function generateInsertId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to fallback
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}

export interface AnalyticsTraits {
  email?: string;
  [key: string]: unknown;
}

export interface AnalyticsClient {
  readonly enabled: boolean;
  /** Must be called once, after auth hydration settles. Idempotent. */
  bootstrap(cachedUserId?: string | null): void;
  identify(distinctId: string, traits?: AnalyticsTraits): void;
  reset(): void;
  capture(event: string, properties?: Record<string, unknown>): void;
  register(properties: Record<string, unknown>): void;
  group(groupType: string, groupKey: string, properties?: Record<string, unknown>): void;
  optIn(): void;
  optOut(): void;
}

type QueuedCall =
  | { kind: "identify"; distinctId: string; traits?: AnalyticsTraits }
  | { kind: "reset" }
  | { kind: "capture"; event: string; properties?: Record<string, unknown> }
  | { kind: "register"; properties: Record<string, unknown> }
  | { kind: "group"; groupType: string; groupKey: string; properties?: Record<string, unknown> }
  | { kind: "optIn" }
  | { kind: "optOut" };

// Build the no-op shape used when PostHog is disabled (missing key or dev).
// `bootstrap` is a no-op so callers don't have to branch.
function noopClient(): AnalyticsClient {
  return {
    enabled: false,
    bootstrap: () => {},
    identify: () => {},
    reset: () => {},
    capture: () => {},
    register: () => {},
    group: () => {},
    optIn: () => {},
    optOut: () => {},
  };
}

function createClient(): AnalyticsClient {
  if (IS_CONNECTION_SESSION) return noopClient();
  // PostHog is production-only. No staging project exists — staging + dev
  // must never pollute the single production PostHog project. The activity
  // ledger dual-write is gated separately so admin activity keeps working if
  // the PostHog key is absent. Defense in depth: both API_ENV and
  // IS_DEVELOPMENT are checked because a `vite build --mode production` run
  // locally sets API_ENV=production but IS_DEVELOPMENT can still be true
  // depending on build mode.
  const posthogEnabled = Boolean(POSTHOG_KEY) && CONFIG.API_ENV === "production" && !CONFIG.IS_DEVELOPMENT;
  if (!posthogEnabled && !ACTIVITY_LEDGER_ENABLED) return noopClient();

  const source = resolveSource();
  const isNative = Capacitor.isNativePlatform();

  // Pre-init state: queue calls until bootstrap() runs. Bounded so that
  // a theoretical never-fires-bootstrap scenario (AuthContext hang, React
  // crash loop) can't leak memory via unbounded queue growth. At MAX_QUEUE
  // we drop oldest events (FIFO eviction) so the most recent signal is
  // preserved. In practice the queue should drain within 200ms of first
  // render; this cap exists purely as a safety net.
  const MAX_QUEUE = 500;
  let initialized = false;
  let queue: QueuedCall[] = [];
  let queueDroppedCount = 0;

  const enqueue = (call: QueuedCall) => {
    if (queue.length >= MAX_QUEUE) {
      queue.shift(); // drop oldest
      queueDroppedCount++;
      if (queueDroppedCount === 1 || queueDroppedCount % 100 === 0) {
        console.warn(`[analytics] queue full (${MAX_QUEUE}); dropped ${queueDroppedCount} events`);
      }
    }
    queue.push(call);
  };

  // Dedupe identify() calls. AuthContext re-runs identify on every
  // profile-hydration path (cached user, fresh fetch, stale-is_gated refetch,
  // login, register) — ~9 call sites. PostHog is idempotent on identify but
  // each call is a network event that bills and pollutes insights.
  //
  // The dedup key is (distinctId + traits) serialized. Keying on distinctId
  // alone silently swallows trait updates (e.g. the future email-change
  // flow calling identify(same_user_id, { email: newEmail })) — that's the
  // Data Model Skeptic's M7 failure mode. Serializing traits costs a few
  // µs per call but preserves update semantics.
  let lastIdentifyKey: string | null = null;

  const safe = <A extends unknown[]>(fn: (...args: A) => void) => (...args: A) => {
    try {
      fn(...args);
    } catch (err) {
      console.warn("[analytics] call failed", err);
    }
  };

  const identifyKey = (distinctId: string, traits?: AnalyticsTraits): string => {
    // Stable ordering: JSON.stringify with sorted trait keys so {a:1,b:2}
    // and {b:2,a:1} produce the same key. Prevents false positives where
    // two semantically-equal identify calls aren't dedup'd.
    if (!traits) return `${distinctId}|`;
    const sortedKeys = Object.keys(traits).sort();
    const sortedTraits: Record<string, unknown> = {};
    for (const k of sortedKeys) sortedTraits[k] = traits[k];
    return `${distinctId}|${JSON.stringify(sortedTraits)}`;
  };

  const runIdentify = (distinctId: string, traits?: AnalyticsTraits) => {
    if (!posthogEnabled) return;
    const key = identifyKey(distinctId, traits);
    if (key === lastIdentifyKey) return;
    lastIdentifyKey = key;
    posthog.identify(distinctId, traits);
  };

  const runCapture = (event: string, properties?: Record<string, unknown>) => {
    if (posthogEnabled) {
      posthog.capture(event, properties);
    }
    captureActivityLedger(event, properties);
  };

  const runCall = (call: QueuedCall) => {
    switch (call.kind) {
      case "identify":
        runIdentify(call.distinctId, call.traits);
        break;
      case "reset":
        lastIdentifyKey = null;
        if (posthogEnabled) posthog.reset();
        break;
      case "capture":
        runCapture(call.event, call.properties);
        break;
      case "register":
        if (posthogEnabled) posthog.register(call.properties);
        break;
      case "group":
        if (posthogEnabled) posthog.group(call.groupType, call.groupKey, call.properties);
        break;
      case "optIn":
        if (posthogEnabled) posthog.opt_in_capturing();
        break;
      case "optOut":
        if (posthogEnabled) posthog.opt_out_capturing();
        break;
    }
  };

  return {
    enabled: posthogEnabled,

    bootstrap: safe((cachedUserId?: string | null) => {
      if (initialized) return;
      initialized = true;

      if (posthogEnabled) {
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          // Only create PostHog person profiles for identified users.
          person_profiles: "identified_only",
          // Cookie persistence is broken on capacitor:// scheme in WKWebView.
          persistence: isNative ? "localStorage" : "localStorage+cookie",
          cross_subdomain_cookie: false,
          // We fire $pageview manually from App.tsx.
          capture_pageview: false,
          // Session replay: full-rate until billing matters.
          disable_session_recording: false,
          session_recording: {
            maskAllInputs: true,
            maskTextSelector: "*",
            recordBody: false,
            recordHeaders: false,
          },
          mask_all_text: true,
          mask_all_element_attributes: true,
          opt_out_capturing_by_default: false,
          // Bootstrap with the cached user_id — skips the anonymous-distinct-id
          // stage on authenticated cold starts. Without this, every cold
          // launch of an already-signed-in user creates an anon profile, then
          // immediately merges it when AuthContext calls identify(). That
          // merge is a billable event and bloats person-cardinality.
          ...(cachedUserId ? { bootstrap: { distinctID: cachedUserId } } : {}),
        });

        // Register super-properties synchronously so queued captures
        // inherit them when flushed. environment is hardcoded to "production"
        // because init is already gated on API_ENV === "production" above.
        posthog.register({
          source,
          environment: CONFIG.API_ENV,
          app_version: APP_VERSION,
          native_platform: Capacitor.getPlatform(),
        });
      }

      // If we bootstrapped with a user_id, seed the dedup key with the
      // bootstrap identity (no traits) so a subsequent identify() with the
      // same user_id + empty traits short-circuits. An identify() that
      // supplies traits (e.g. {email}) will have a different key and fire
      // through — which is the intended behavior, so PostHog person traits
      // get populated on first real identify after bootstrap.
      if (posthogEnabled && cachedUserId) {
        lastIdentifyKey = identifyKey(cachedUserId, undefined);
      }

      // Flush queued calls.
      const pending = queue;
      queue = [];
      for (const call of pending) {
        try {
          runCall(call);
        } catch (err) {
          console.warn("[analytics] queued call failed", err);
        }
      }
    }),

    identify: safe((distinctId: string, traits?: AnalyticsTraits) => {
      if (!initialized) {
        enqueue({ kind: "identify", distinctId, traits });
        return;
      }
      runIdentify(distinctId, traits);
    }),

    reset: safe(() => {
      if (!initialized) {
        enqueue({ kind: "reset" });
        return;
      }
      lastIdentifyKey = null;
      if (posthogEnabled) posthog.reset();
    }),

    capture: safe((event: string, properties?: Record<string, unknown>) => {
      // Stamp an insert_id at capture-call time so PostHog can dedupe
      // network retries + the client/server overlap cases where both tiers
      // might fire for one logical user action. Stable from queue through
      // flush: if the event is queued pre-bootstrap, this same id rides
      // through to ingest.
      const withId: Record<string, unknown> = {
        ...(properties ?? {}),
        $insert_id: generateInsertId(),
      };
      if (!initialized) {
        enqueue({ kind: "capture", event, properties: withId });
        return;
      }
      runCapture(event, withId);
    }),

    register: safe((properties: Record<string, unknown>) => {
      if (!initialized) {
        enqueue({ kind: "register", properties });
        return;
      }
      if (posthogEnabled) posthog.register(properties);
    }),

    group: safe((groupType: string, groupKey: string, properties?: Record<string, unknown>) => {
      if (!initialized) {
        enqueue({ kind: "group", groupType, groupKey, properties });
        return;
      }
      if (posthogEnabled) posthog.group(groupType, groupKey, properties);
    }),

    optIn: safe(() => {
      if (!initialized) {
        enqueue({ kind: "optIn" });
        return;
      }
      if (posthogEnabled) posthog.opt_in_capturing();
    }),

    optOut: safe(() => {
      if (!initialized) {
        enqueue({ kind: "optOut" });
        return;
      }
      if (posthogEnabled) posthog.opt_out_capturing();
    }),
  };
}

export const analytics: AnalyticsClient = createClient();
